import {Heading, Pane, Text} from "evergreen-ui";
import React, {useCallback, useState} from "react";
import {useCurrentUser} from "security";
import {ClientManagementLayout} from "../../layouts/ClientManagementLayout";
import {PORTAL_NAVY} from "../../../common";
import {hasContactEditAccess} from "../../domain/security";
import {BusinessContactTable} from "./BusinessContactTable";
import {ContactDrawer} from "./ContactDrawer";
import {ContactHistoryPanel} from "./ContactHistoryPanel";
import {DuplicateContactDialog} from "./DuplicateContactDialog";
import {ContactForm, ContactFormSubmission} from "./ContactForm";
import {useBusinessContactMutation} from "./useBusinessContactMutation";
import {BusinessContactRow} from "./domain/types";

/**
 * Nothing open, the form on a contact (null for a new one), or the history of a contact. One drawer
 * serves both: the pencil and the clock swap what it holds rather than opening a second one.
 */
type DrawerState =
    | {open: false}
    | {open: true; mode: 'edit'; contact: BusinessContactRow | null}
    | {open: true; mode: 'history'; contact: BusinessContactRow};
type OpenDrawer = Exclude<DrawerState, {open: false}>;

/** The drawer's header text. A closed drawer renders nothing, so the empty title is never read. */
const drawerTitle = (drawer: DrawerState): string => {
    if (!drawer.open) {
        return '';
    }
    if (drawer.mode === 'history') {
        return 'Contact History';
    }
    return drawer.contact ? 'Edit Contact' : 'Add Contact';
};

/**
 * The glyph before the drawer's title. The history mockup heads the column with the words alone, so
 * the history arm asks for no glyph and the form keeps the drawer's pencil.
 */
const drawerIcon = (drawer: DrawerState): React.ReactNode =>
    drawer.open && drawer.mode === 'history' ? null : undefined;

const ADD_PINK = "#D00261";

/** The toolbar button, in the shape of Roster Management's Upload Roster: pink, white text, a glyph. */
const AddContactButton = ({onClick}: {onClick: () => void}) => (
    <button type="button" onClick={onClick}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                height: 40,
                padding: "0 20px",
                marginRight: 16,
                border: "none",
                borderRadius: 6,
                background: ADD_PINK,
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
            }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="#fff" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14"/>
            <path d="M5 12h14"/>
        </svg>
        Add Contact
    </button>
);

export const BusinessContactPage = () => {
    const canEdit = hasContactEditAccess(useCurrentUser());
    const [drawer, setDrawer] = useState<DrawerState>({open: false});
    // The page owns the refresh because the table owns its query privately. Bumping this is how a
    // save gets the grid to refetch.
    const [refreshToken, setRefreshToken] = useState(0);

    const onSaved = useCallback(() => {
        setDrawer({open: false});
        setRefreshToken((token) => token + 1);
    }, []);
    const {save, saving, errorMessage, clearError, duplicateMessage, duplicatePending, confirmDuplicate,
        dismissDuplicate} = useBusinessContactMutation(onSaved);

    // The drawer holds still while a save is in flight and while a duplicate question is unanswered.
    // Without this a user could click another row's pencil or clock mid-save and have the first
    // contact's answer land on the second contact's form, or on no form at all: a duplicate question
    // about a record they are no longer looking at, and a toast that reads as confirmation of the one
    // they are. The dialog's overlay covers the page while the question is up, so the second guard is
    // a belt for the keyboard and for any change to that overlay.
    const drawerIsBusy = saving || duplicatePending;
    const closeDrawer = useCallback(() => {
        if (!drawerIsBusy) {
            setDrawer({open: false});
        }
    }, [drawerIsBusy]);
    // Takes the whole open state rather than (mode, contact). The history arm needs a contact and the
    // edit arm allows none; the object form keeps that difference in the type.
    const openOn = useCallback((next: OpenDrawer) => {
        if (drawerIsBusy) {
            return;
        }
        clearError();
        setDrawer(next);
    }, [drawerIsBusy, clearError]);
    const editContact = useCallback(
        (contact: BusinessContactRow) => openOn({open: true, mode: 'edit', contact}), [openOn]);
    const addContact = useCallback(() => openOn({open: true, mode: 'edit', contact: null}), [openOn]);
    const historyContact = useCallback(
        (contact: BusinessContactRow) => openOn({open: true, mode: 'history', contact}), [openOn]);

    const contactDrawer = (
        <ContactDrawer open={drawer.open}
                       title={drawerTitle(drawer)}
                       icon={drawerIcon(drawer)}
                       onClose={closeDrawer}>
            {drawer.open && drawer.mode === 'edit' && (
                // The key is what makes clicking a second row's pencil work: react-hook-form
                // reads defaultValues once, so the form has to remount rather than re-render.
                <ContactForm key={drawer.contact?.guid ?? 'new'}
                             contact={drawer.contact}
                             saving={saving}
                             errorMessage={errorMessage}
                             onCancel={closeDrawer}
                             onSubmit={(values: ContactFormSubmission) => save(drawer.contact, values)}/>
            )}
            {drawer.open && drawer.mode === 'history' && (
                <ContactHistoryPanel key={drawer.contact.guid} contact={drawer.contact} onClose={closeDrawer}/>
            )}
        </ContactDrawer>
    );

    return (
        <ClientManagementLayout customSelection={'Contact Management'} sidePanel={contactDrawer}>
            <Pane className="m-5 p-5 bg-white shadow-md rounded flex flex-col portal-type-scale" flexGrow={1}
                  minHeight="calc(100vh - 100px - 260px - 36px)">
                <Heading size={900} fontSize={32} color={PORTAL_NAVY} marginBottom={8}>Contact Management</Heading>
                <Text size={500} marginBottom={24}>View and edit your organization's business functions with CCS.</Text>
                <BusinessContactTable onEdit={editContact}
                                      onHistory={historyContact}
                                      refreshToken={refreshToken}
                                      rightActions={canEdit && <AddContactButton onClick={addContact}/>}/>
            </Pane>
            <DuplicateContactDialog isShown={duplicateMessage !== ''}
                                    busy={saving}
                                    message={duplicateMessage}
                                    onConfirmed={confirmDuplicate}
                                    onDismissed={dismissDuplicate}/>
        </ClientManagementLayout>
    );
};
